// src/components/document-sets/DocumentSetSidebarList.js
import React, { useState, useMemo } from 'react';
import { FolderLock, ChevronDown, ChevronRight, Trash2, Info } from 'lucide-react';
import { useGuideForce } from '../../contexts/GuideForceContext';
import DocumentSetSidebarPill from './DocumentSetSidebarPill';
import DocumentSetPill from './DocumentSetPill';
import DeleteAlertDialog from '../ui/DeleteAlertDialog';

/**
 * Document Set Sidebar List
 *
 * Features:
 * - Displays document sets as nested items under a selected case
 * - Separates locked sets into a collapsible "Locked Sets" folder
 * - Fade in/slide down animations when case is selected
 * - Delete confirmation dialog
 * - Filtering by selected set
 * 
 * FIXED: Changed transition-all to specific properties (opacity, transform)
 * This prevents position changes from being animated when editing pills
 */
const DocumentSetSidebarList = ({
  documentSets = [],
  selectedSetId = null,
  onSelectSet,
  onToggleArchive,
  onDeleteSet,
  onUpdateSetName,
  isVisible = true,
}) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [setToDelete, setSetToDelete] = useState(null);
  const [isLockedFolderOpen, setIsLockedFolderOpen] = useState(false);

  const { tagGuideStep } = useGuideForce();

  // Separate archived and active sets, and sort by lastModified descending
  const { activeSets, archivedSets } = useMemo(() => {
    const sortByLastModified = (sets) => {
      return [...sets].sort((a, b) => {
        const aDate = a.lastModified?.toDate ? a.lastModified.toDate() : new Date(a.lastModified || 0);
        const bDate = b.lastModified?.toDate ? b.lastModified.toDate() : new Date(b.lastModified || 0);
        return bDate - aDate;
      });
    };

    const active = sortByLastModified(documentSets.filter(set => !set.archived));
    const archived = sortByLastModified(documentSets.filter(set => set.archived));
    return { activeSets: active, archivedSets: archived };
  }, [documentSets]);

  const showSkeletonTag = tagGuideStep === 4 && activeSets.length === 0;

  const handleDeleteClick = (setId) => {
    const set = documentSets.find(s => s.id === setId);
    setSetToDelete(set);
    setDeleteDialogOpen(true);
  };

  if (documentSets.length === 0 && !showSkeletonTag) return null;

  return (
    <>
      <div
        className={`
          flex flex-col items-start gap-[2px] px-app-xs
          transition-all duration-300 ease-in-out
          ${isVisible
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 -translate-y-2'
          }
        `}
      >
        {/* Active Sets */}
        {activeSets.map((set, index) => (
          index === 0 ? (
            <div key={set.id} data-guide-target="tag-guide-first-tag">
              <DocumentSetSidebarPill
                documentSet={set}
                isSelected={selectedSetId === set.id}
                isArchived={false}
                onSelect={onSelectSet}
                onToggleArchive={onToggleArchive}
                onDelete={handleDeleteClick}
                onUpdateName={onUpdateSetName}
                documentSets={documentSets}
              />
            </div>
          ) : (
            <DocumentSetSidebarPill
              key={set.id}
              documentSet={set}
              isSelected={selectedSetId === set.id}
              isArchived={false}
              onSelect={onSelectSet}
              onToggleArchive={onToggleArchive}
              onDelete={handleDeleteClick}
              onUpdateName={onUpdateSetName}
              documentSets={documentSets}
            />
          )
        ))}

        {showSkeletonTag && (
          <div
            data-guide-target="tag-guide-first-tag"
            className="inline-flex items-center px-3 rounded-full h-[24px] border pointer-events-none select-none"
            style={{ backgroundColor: '#DBEAFE', borderColor: '#BFDBFE' }}
          >
            <div className="flex items-center gap-1">
              <div className="w-5 h-2.5 rounded-app-2xs" style={{ backgroundColor: '#93C5FD' }} />
              <div className="w-8 h-2.5 rounded-app-2xs" style={{ backgroundColor: '#93C5FD' }} />
            </div>
          </div>
        )}

        {/* Archived Sets Folder */}
        {archivedSets.length > 0 && (
          <div className="mt-1">
            {/* Folder Header */}
            <button
              onClick={() => setIsLockedFolderOpen(!isLockedFolderOpen)}
              className="w-full h-comp-4xl px-app-xs flex items-center gap-app-xs rounded-app-sm hover:bg-app-gray-400 transition-colors group"
            >
              {isLockedFolderOpen ? (
                <ChevronDown size={14} className="text-app-gray-600 flex-shrink-0" />
              ) : (
                <ChevronRight size={14} className="text-app-gray-600 flex-shrink-0" />
              )}

              <FolderLock size={14} className="text-app-gray-600 flex-shrink-0" />

              <span className="text-app-xs text-app-gray-600 font-medium">
                Completed
              </span>
            </button>

            {/* Archived Sets List */}
            {isLockedFolderOpen && (
              <div className="ml-app-md space-y-[2px] mt-[2px]">
                {archivedSets.map((set, index) => (
                  <div
                    key={set.id}
                  >
                    <DocumentSetSidebarPill
                      documentSet={set}
                      isSelected={selectedSetId === set.id}
                      isArchived={true}
                      onSelect={onSelectSet}
                      onToggleArchive={onToggleArchive}
                      onDelete={handleDeleteClick}
                      onUpdateName={onUpdateSetName}
                      documentSets={documentSets}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <DeleteAlertDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={
          <div className="flex items-center gap-4 min-w-0">
            <span className="flex-shrink-0">Deleting tag:</span>
            {setToDelete && (
              <DocumentSetPill
                documentSet={setToDelete}
                showRemove={false}
                isReadOnly={true}
                className="inline-flex min-w-0 text-app-sm"
              />
            )}
          </div>
        }
        warnings={[
          <>This tag will be removed from all documents but will not delete them. This can't be undone.</>,
        ]}
        showBullets={false}
        warningIcon={<Info className="w-comp-5xl h-comp-5xl" />}
        warningIconColor="text-app-info"
        confirmButtonText="Delete Tag"
        confirmButtonIcon={<Trash2 className="w-comp-lg h-comp-lg" />}
        onConfirm={() => {
          if (setToDelete) {
            onDeleteSet?.(setToDelete.id);
            setSetToDelete(null);
          }
        }}
      />
    </>
  );
};

export default DocumentSetSidebarList;